/**
 * CourseForm.js
 * Component for creating and editing courses
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Alert,
  Spinner,
  Nav,
  Tab
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { courseService, commonService } from '../../services/api';
import { getAcademicYearOptions } from '../../helpers/timeHelper';

const CourseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingAcademicYear, setLoadingAcademicYear] = useState(true);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    academicYear: '', // Sarà caricato dal server
    description: ''
  });

  // Anno accademico corrente dal backend
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Load current academic year
  useEffect(() => {
    const loadCurrentAcademicYear = async () => {
      try {
        setLoadingAcademicYear(true);
        const response = await commonService.getCurrentAcademicYear();
        
        if (response.success) {
          setCurrentAcademicYear(response.data);
          // Imposta l'anno accademico solo se non stiamo editando
          if (!isEditing) {
            setFormData(prev => ({
              ...prev,
              academicYear: response.data
            }));
          }
        } else {
          // Fallback al calcolo lato client in caso di errore
          const fallbackYear = new Date().getMonth() >= 8 ? new Date().getFullYear() : new Date().getFullYear() - 1;
          setCurrentAcademicYear(fallbackYear);
          if (!isEditing) {
            setFormData(prev => ({
              ...prev,
              academicYear: fallbackYear
            }));
          }
        }
      } catch (err) {
        console.error('Error loading current academic year:', err);
        // Fallback al calcolo lato client in caso di errore
        const fallbackYear = new Date().getMonth() >= 8 ? new Date().getFullYear() : new Date().getFullYear() - 1;
        setCurrentAcademicYear(fallbackYear);
        if (!isEditing) {
          setFormData(prev => ({
            ...prev,
            academicYear: fallbackYear
          }));
        }
      } finally {
        setLoadingAcademicYear(false);
      }
    };

    loadCurrentAcademicYear();
  }, [isEditing]);

  const loadCourseData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await courseService.getCourse(id);
      
      if (response.success) {
        const course = response.data;
        setFormData({
          name: course.name,
          academicYear: course.academicYear,
          description: course.description || ''
        });
      } else {
        setError(response.message || 'Errore nel caricamento del corso');
      }
    } catch (err) {
      setError('Errore nel caricamento del corso');
      console.error('Error loading course:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load course data if editing
  useEffect(() => {
    if (isEditing) {
      loadCourseData();
    }
  }, [isEditing, loadCourseData]);

  // Handle form changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Il nome del corso è obbligatorio';
    }

    if (!formData.academicYear) {
      errors.academicYear = 'L\'anno sportivo è obbligatorio';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      let response;
      if (isEditing) {
        response = await courseService.updateCourse(id, formData);
      } else {
        response = await courseService.createCourse(formData);
      }

      if (response.success) {
        setSuccess(response.message);
        // Redirect to courses list after creation or update
        setTimeout(() => {
          navigate('/courses');
        }, 1000);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Errore nel salvataggio del corso');
      console.error('Error saving course:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="course-form fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEditing ? 'Modifica Corso' : 'Nuovo Corso'}</h2>
        <Button variant="outline-secondary" onClick={() => navigate('/courses')}>
          <i className="fas fa-arrow-left me-2"></i>
          Torna alla lista
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Tab.Container defaultActiveKey="details">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="details">Dettagli Corso</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Course Details Tab */}
          <Tab.Pane eventKey="details">
            <Card>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="name">
                        <Form.Label>Nome Corso *</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          isInvalid={!!validationErrors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="academicYear">
                        <Form.Label>Anno Sportivo *</Form.Label>
                        <Form.Select
                          value={formData.academicYear}
                          onChange={(e) => handleInputChange('academicYear', parseInt(e.target.value) || '')}
                          isInvalid={!!validationErrors.academicYear}
                          disabled={loadingAcademicYear}
                        >
                          {loadingAcademicYear ? (
                            <option value="">Caricamento...</option>
                          ) : (
                            getAcademicYearOptions(currentAcademicYear).map(year => (
                              <option key={year} value={year}>
                                {year}/{year + 1}
                              </option>
                            ))
                          )}
                        </Form.Select>
                        {loadingAcademicYear && (
                          <Form.Text className="text-muted">
                            <Spinner animation="border" size="sm" className="me-1" />
                            Caricamento anno sportivo corrente...
                          </Form.Text>
                        )}
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.academicYear}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Descrizione</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? <Spinner animation="border" size="sm" /> : (isEditing ? 'Aggiorna Corso' : 'Crea Corso')}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/courses')}>
                      Annulla
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default CourseForm;